import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { format } from "date-fns";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import DataLoadErrorAlert from "components/common/DataLoadErrorAlert";
import FeatureGate from "shared/components/FeatureGate";
import { useAuth } from "hooks/useAuth";
import { db } from "services/firebase";
import useFacilityGrantedEntitlements from "hooks/useFacilityGrantedEntitlements";
import {
  resolveEffectivePatientAccess,
  tierAtLeast,
} from "@healthdesk/shared-utils";

const ResultsCommsPageInner = () => {
  const { user, subscriptionTier } = useAuth();
  const { grants } = useFacilityGrantedEntitlements();
  const effectiveTier = resolveEffectivePatientAccess(subscriptionTier, grants);
  const canSeeDetail = tierAtLeast(effectiveTier, "medium");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!user?.uid) return undefined;
    setLoading(true);
    const unsub = db
      .collection("users")
      .doc(user.uid)
      .collection("resultNotifications")
      .orderBy("readyAt", "desc")
      .onSnapshot(
        (snap) => {
          setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          setLoading(false);
        },
        () => {
          setLoadError(true);
          setLoading(false);
        },
      );
    return () => unsub();
  }, [user?.uid]);

  return (
    <Box>
      <DashboardPageHeader
        title="Your results"
        subtitle="Notifications when labs or imaging results are ready."
      />
      {!canSeeDetail ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Your plan includes readiness notifications. Upgrade or visit a
          participating clinic to view result details.
        </Alert>
      ) : null}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : loadError ? (
        <DataLoadErrorAlert />
      ) : items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No result notifications yet.
        </Typography>
      ) : (
        <List>
          {items.map((item) => (
            <ListItem key={item.id} alignItems="flex-start">
              <ListItemText
                primary={item.headline}
                secondary={
                  <>
                    {item.readyAt?.toDate ? (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Ready {format(item.readyAt.toDate(), "MMM d, yyyy")}
                      </Typography>
                    ) : null}
                    {canSeeDetail && item.detail ? (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {item.detail}
                      </Typography>
                    ) : null}
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

const ResultsCommsPage = () => (
  <FeatureGate feature="results-comms-notify" showUpgradePrompt>
    <ResultsCommsPageInner />
  </FeatureGate>
);

export default ResultsCommsPage;
